import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { AdminLayout } from "./Dashboard";
import DiscoverWidget from "@/components/DiscoverWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
    Plus,
    Pencil,
    Trash2,
    Search,
    Trophy,
    PowerOff,
    Power,
} from "lucide-react";

const SPORTS = [
    { id: "football", name: "Football" },
    { id: "basketball", name: "Basketball" },
    { id: "rugby", name: "Rugby" },
    { id: "athletics", name: "Athletics" },
    { id: "boxing", name: "Boxing" },
];

const COMPETITIONS = {
    football: [
        { id: "epl", name: "Premier League", country: "england" },
        { id: "laliga", name: "La Liga", country: "spain" },
        { id: "serie_a", name: "Serie A", country: "italy" },
        { id: "bundesliga", name: "Bundesliga", country: "germany" },
        { id: "ligue1", name: "Ligue 1", country: "france" },
        { id: "ucl", name: "Champions League", country: "europe" },
        { id: "uel", name: "Europa League", country: "europe" },
        { id: "nbc_premier", name: "NBC Premier League", country: "tanzania" },
        { id: "caf_cl", name: "CAF Champions League", country: "africa" },
        { id: "afcon", name: "AFCON", country: "africa" },
    ],
    basketball: [
        { id: "nba", name: "NBA", country: "usa" },
    ],
    rugby: [],
    athletics: [],
    boxing: [],
};

const STATUSES = [
    { id: "scheduled", name: "Scheduled" },
    { id: "live", name: "Live" },
    { id: "halftime", name: "Half Time" },
    { id: "finished", name: "Finished (FT)" },
    { id: "postponed", name: "Postponed" },
    { id: "cancelled", name: "Cancelled" },
];

const initialForm = {
    sport: "football",
    competition_id: "epl",
    country: "england",
    home_team: "",
    away_team: "",
    home_team_logo: "",
    away_team_logo: "",
    venue: "",
    match_date: "",
    status: "finished",
    score_home: 0,
    score_away: 0,
    is_enabled: true,
};

const MatchManagement = () => {
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [sportFilter, setSportFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [showDialog, setShowDialog] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(initialForm);

    useEffect(() => {
        fetchMatches();
    }, [sportFilter, statusFilter]);

    const authHeaders = () => {
        const token = localStorage.getItem("dimbani_token");
        return { Authorization: `Bearer ${token}` };
    };

    const fetchMatches = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: 100, enabled_only: false });
            if (sportFilter !== "all") params.set("sport", sportFilter);
            if (statusFilter !== "all") params.set("status", statusFilter);
            const { data } = await axios.get(`${API}/matches?${params.toString()}`);
            setMatches(data || []);
        } catch (error) {
            toast.error("Failed to load matches");
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditingId(null);
        setForm({
            ...initialForm,
            match_date: new Date().toISOString().slice(0, 16),
        });
        setShowDialog(true);
    };

    const openEdit = (match) => {
        setEditingId(match.id);
        setForm({
            sport: match.sport,
            competition_id: match.competition_id,
            country: match.country,
            home_team: match.home_team,
            away_team: match.away_team,
            home_team_logo: match.home_team_logo || "",
            away_team_logo: match.away_team_logo || "",
            venue: match.venue || "",
            match_date: new Date(match.match_date).toISOString().slice(0, 16),
            status: match.status,
            score_home: match.score_home || 0,
            score_away: match.score_away || 0,
            is_enabled: match.is_enabled !== false,
        });
        setShowDialog(true);
    };

    const handleSave = async () => {
        if (!form.home_team || !form.away_team || !form.match_date) {
            toast.error("Home team, away team, and match date are required");
            return;
        }
        const payload = {
            ...form,
            score_home: Number(form.score_home) || 0,
            score_away: Number(form.score_away) || 0,
            match_date: new Date(form.match_date).toISOString(),
        };
        try {
            if (editingId) {
                await axios.put(`${API}/matches/${editingId}`, payload, {
                    headers: authHeaders(),
                });
                toast.success("Match updated");
            } else {
                await axios.post(`${API}/matches`, payload, {
                    headers: authHeaders(),
                });
                toast.success("Match created");
            }
            setShowDialog(false);
            fetchMatches();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to save match");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this match? This cannot be undone.")) return;
        try {
            await axios.delete(`${API}/matches/${id}`, { headers: authHeaders() });
            toast.success("Match deleted");
            fetchMatches();
        } catch {
            toast.error("Failed to delete match");
        }
    };

    const handleToggle = async (id) => {
        try {
            await axios.patch(
                `${API}/matches/${id}/toggle`,
                {},
                { headers: authHeaders() }
            );
            toast.success("Match visibility updated");
            fetchMatches();
        } catch {
            toast.error("Failed to toggle match");
        }
    };

    const filtered = matches.filter((m) => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
            m.home_team?.toLowerCase().includes(s) ||
            m.away_team?.toLowerCase().includes(s) ||
            m.competition_id?.toLowerCase().includes(s)
        );
    });

    const getStatusBadge = (status) => {
        const map = {
            live: "bg-red-500/20 text-red-400 border-red-500/30",
            finished: "bg-electric/10 text-electric border-electric/30",
            scheduled: "bg-blue-500/10 text-blue-400 border-blue-500/30",
            halftime: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
            postponed: "bg-orange-500/10 text-orange-400 border-orange-500/30",
            cancelled: "bg-gray-500/10 text-gray-400 border-gray-500/30",
        };
        return map[status] || "bg-white/5 text-white/60";
    };

    const competitionsForSport = COMPETITIONS[form.sport] || [];

    return (
        <AdminLayout title="Match Results">
            <div className="space-y-6" data-testid="admin-match-management">
                {/* Discover fixtures from web — import + auto-predict + feature */}
                <DiscoverWidget defaultKind="fixture" />
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Trophy className="h-6 w-6 text-electric" />
                        <div>
                            <h1 className="text-2xl font-heading font-bold text-white">
                                Match Results
                            </h1>
                            <p className="text-white/50 text-sm">
                                Manually add, edit, or disable match results not available from the feed.
                            </p>
                        </div>
                    </div>
                    <Button
                        onClick={openCreate}
                        className="bg-electric hover:bg-vodaredDark text-white"
                        data-testid="add-match-btn"
                    >
                        <Plus className="h-4 w-4 mr-2" /> Add Match
                    </Button>
                </div>

                {/* Filters */}
                <Card className="bg-paper border-border p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="relative md:col-span-2">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                            <Input
                                placeholder="Search by team or competition..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 bg-subtle border-border text-white"
                                data-testid="match-search-input"
                            />
                        </div>
                        <Select value={sportFilter} onValueChange={setSportFilter}>
                            <SelectTrigger className="bg-subtle border-border text-white">
                                <SelectValue placeholder="Sport" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Sports</SelectItem>
                                {SPORTS.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>
                                        {s.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="bg-subtle border-border text-white">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Statuses</SelectItem>
                                {STATUSES.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>
                                        {s.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </Card>

                {/* Matches Table */}
                <Card className="bg-paper border-border overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-white/40">Loading...</div>
                    ) : filtered.length === 0 ? (
                        <div className="p-8 text-center">
                            <Trophy className="h-10 w-10 text-white/20 mx-auto mb-2" />
                            <p className="text-white/60">
                                No matches yet. Click "Add Match" to create one manually.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-subtle border-b border-border">
                                    <tr className="text-left text-white/60 text-xs uppercase">
                                        <th className="px-4 py-3">Competition</th>
                                        <th className="px-4 py-3">Home</th>
                                        <th className="px-4 py-3 text-center">Score</th>
                                        <th className="px-4 py-3">Away</th>
                                        <th className="px-4 py-3">Date</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3 text-center">Enabled</th>
                                        <th className="px-4 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((m, idx) => (
                                        <tr
                                            key={m.id}
                                            className="border-b border-border/50 hover:bg-subtle/50 transition-colors"
                                            data-testid={`match-row-${idx}`}
                                        >
                                            <td className="px-4 py-3 text-white/70 text-xs">
                                                {m.competition_name || m.competition_id}
                                            </td>
                                            <td className="px-4 py-3 text-white font-medium">
                                                {m.home_team}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="font-mono font-bold text-white">
                                                    {m.score_home} - {m.score_away}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-white font-medium">
                                                {m.away_team}
                                            </td>
                                            <td className="px-4 py-3 text-white/60 text-xs">
                                                {new Date(m.match_date).toLocaleDateString()}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge
                                                    variant="outline"
                                                    className={`${getStatusBadge(m.status)} text-xs`}
                                                >
                                                    {m.status}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    onClick={() => handleToggle(m.id)}
                                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                                        m.is_enabled !== false
                                                            ? "bg-electric/10 text-electric"
                                                            : "bg-white/5 text-white/40"
                                                    }`}
                                                    data-testid={`toggle-match-${idx}`}
                                                >
                                                    {m.is_enabled !== false ? (
                                                        <>
                                                            <Power className="h-3 w-3" /> On
                                                        </>
                                                    ) : (
                                                        <>
                                                            <PowerOff className="h-3 w-3" /> Off
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => openEdit(m)}
                                                    className="text-white/70 hover:text-electric"
                                                    data-testid={`edit-match-${idx}`}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handleDelete(m.id)}
                                                    className="text-white/70 hover:text-red-400"
                                                    data-testid={`delete-match-${idx}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>

            {/* Create / Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="bg-paper border-border text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white font-heading">
                            {editingId ? "Edit Match" : "Add Match"}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                        <div>
                            <Label className="text-white/70 mb-1 block">Sport</Label>
                            <Select
                                value={form.sport}
                                onValueChange={(v) =>
                                    setForm({
                                        ...form,
                                        sport: v,
                                        competition_id: COMPETITIONS[v]?.[0]?.id || "",
                                        country: COMPETITIONS[v]?.[0]?.country || "",
                                    })
                                }
                            >
                                <SelectTrigger className="bg-subtle border-border">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SPORTS.map((s) => (
                                        <SelectItem key={s.id} value={s.id}>
                                            {s.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-white/70 mb-1 block">Competition</Label>
                            <Select
                                value={form.competition_id}
                                onValueChange={(v) => {
                                    const comp = competitionsForSport.find((c) => c.id === v);
                                    setForm({
                                        ...form,
                                        competition_id: v,
                                        country: comp?.country || form.country,
                                    });
                                }}
                            >
                                <SelectTrigger className="bg-subtle border-border">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {competitionsForSport.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>
                                            {c.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-white/70 mb-1 block">Home Team</Label>
                            <Input
                                value={form.home_team}
                                onChange={(e) =>
                                    setForm({ ...form, home_team: e.target.value })
                                }
                                className="bg-subtle border-border text-white"
                                data-testid="match-home-team"
                            />
                        </div>

                        <div>
                            <Label className="text-white/70 mb-1 block">Away Team</Label>
                            <Input
                                value={form.away_team}
                                onChange={(e) =>
                                    setForm({ ...form, away_team: e.target.value })
                                }
                                className="bg-subtle border-border text-white"
                                data-testid="match-away-team"
                            />
                        </div>

                        <div>
                            <Label className="text-white/70 mb-1 block">Home Score</Label>
                            <Input
                                type="number"
                                min="0"
                                value={form.score_home}
                                onChange={(e) =>
                                    setForm({ ...form, score_home: e.target.value })
                                }
                                className="bg-subtle border-border text-white"
                                data-testid="match-home-score"
                            />
                        </div>

                        <div>
                            <Label className="text-white/70 mb-1 block">Away Score</Label>
                            <Input
                                type="number"
                                min="0"
                                value={form.score_away}
                                onChange={(e) =>
                                    setForm({ ...form, score_away: e.target.value })
                                }
                                className="bg-subtle border-border text-white"
                                data-testid="match-away-score"
                            />
                        </div>

                        <div>
                            <Label className="text-white/70 mb-1 block">Match Date & Time</Label>
                            <Input
                                type="datetime-local"
                                value={form.match_date}
                                onChange={(e) =>
                                    setForm({ ...form, match_date: e.target.value })
                                }
                                className="bg-subtle border-border text-white"
                            />
                        </div>

                        <div>
                            <Label className="text-white/70 mb-1 block">Status</Label>
                            <Select
                                value={form.status}
                                onValueChange={(v) => setForm({ ...form, status: v })}
                            >
                                <SelectTrigger className="bg-subtle border-border">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {STATUSES.map((s) => (
                                        <SelectItem key={s.id} value={s.id}>
                                            {s.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="md:col-span-2">
                            <Label className="text-white/70 mb-1 block">Venue (optional)</Label>
                            <Input
                                value={form.venue}
                                onChange={(e) => setForm({ ...form, venue: e.target.value })}
                                className="bg-subtle border-border text-white"
                            />
                        </div>

                        <div>
                            <Label className="text-white/70 mb-1 block">Home Logo URL (optional)</Label>
                            <Input
                                value={form.home_team_logo}
                                onChange={(e) =>
                                    setForm({ ...form, home_team_logo: e.target.value })
                                }
                                className="bg-subtle border-border text-white"
                            />
                        </div>

                        <div>
                            <Label className="text-white/70 mb-1 block">Away Logo URL (optional)</Label>
                            <Input
                                value={form.away_team_logo}
                                onChange={(e) =>
                                    setForm({ ...form, away_team_logo: e.target.value })
                                }
                                className="bg-subtle border-border text-white"
                            />
                        </div>

                        <div className="md:col-span-2 flex items-center justify-between bg-subtle p-3 rounded border border-border">
                            <div>
                                <Label className="text-white">Visible to public</Label>
                                <p className="text-white/50 text-xs mt-0.5">
                                    Disable to hide this match from the public scoreboard and match center.
                                </p>
                            </div>
                            <Switch
                                checked={form.is_enabled}
                                onCheckedChange={(v) => setForm({ ...form, is_enabled: v })}
                                data-testid="match-enabled-switch"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowDialog(false)}
                            className="border-border text-white/70"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            className="bg-electric hover:bg-vodaredDark text-white"
                            data-testid="save-match-btn"
                        >
                            {editingId ? "Update Match" : "Create Match"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
};

export default MatchManagement;
